import React from "react";
import { Link } from "react-router-dom";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  // ✅ Clean styles (sans "glace"), responsive, hover gérés sans pseudo-selecteurs
  const styles = {
    footer: {
      backgroundColor: "#1F2937",
      color: "#E5E7EB",
      padding: "3rem 0",
      borderTop: "1px solid #374151",
    },
    container: {
      maxWidth: 1200,
      margin: "0 auto",
      padding: "0 1.5rem",
    },
    topGrid: {
      display: "grid",
      gridTemplateColumns: "2fr 1.2fr 1.6fr",
      gap: "2.5rem",
      marginBottom: "2.25rem",
    },
    sectionTitle: {
      color: "#FFFFFF",
      fontSize: "0.95rem",
      fontWeight: 700,
      marginBottom: "1rem",
      textTransform: "uppercase",
      letterSpacing: "0.08em",
    },
    aboutText: {
      color: "#9CA3AF",
      lineHeight: 1.7,
      margin: 0,
      fontSize: "0.95rem",
    },
    divider: {
      height: 1,
      backgroundColor: "#374151",
      margin: "1.25rem 0",
      opacity: 1,
    },

    // links
    inlineLink: {
      color: "#60A5FA",
      textDecoration: "none",
      display: "inline-flex",
      alignItems: "center",
      gap: "0.5rem",
      fontSize: "0.95rem",
      fontWeight: 600,
    },
    list: {
      listStyle: "none",
      padding: 0,
      margin: 0,
      display: "flex",
      flexDirection: "column",
      gap: "0.75rem",
    },
    navLink: {
      color: "#E5E7EB",
      textDecoration: "none",
      display: "flex",
      alignItems: "center",
      gap: "0.75rem",
      fontSize: "0.95rem",
      padding: "0.35rem 0",
      borderRadius: 6,
      fontWeight: 500,
    },

    // icons
    icon: {
      color: "#60A5FA",
      flexShrink: 0,
    },

    // contact
    contactRow: {
      display: "flex",
      gap: "0.9rem",
      alignItems: "flex-start",
      padding: "0.25rem 0",
    },
    contactLabel: {
      color: "#E5E7EB",
      fontWeight: 600,
      fontSize: "0.95rem",
      marginBottom: "0.15rem",
    },
    contactValue: {
      color: "#9CA3AF",
      fontSize: "0.95rem",
      lineHeight: 1.5,
      margin: 0,
    },

    // button
    loginButton: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "0.6rem",
      backgroundColor: "#2563EB",
      color: "#FFFFFF",
      padding: "0.7rem 1.1rem",
      borderRadius: 10,
      textDecoration: "none",
      marginTop: "0.75rem",
      fontWeight: 700,
      fontSize: "0.95rem",
      width: "fit-content",
    },

    // bottom
    bottom: {
      borderTop: "1px solid #374151",
      paddingTop: "1.25rem",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "1rem",
      flexWrap: "wrap",
    },
    copyright: {
      color: "#9CA3AF",
      fontSize: "0.9rem",
      margin: 0,
    },
    socials: {
      display: "flex",
      gap: "0.75rem",
      alignItems: "center",
    },
    socialBtn: {
      width: 38,
      height: 38,
      borderRadius: 999,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "rgba(255,255,255,0.06)",
      border: "1px solid rgba(255,255,255,0.10)",
      color: "#9CA3AF",
      textDecoration: "none",
    },

    // responsive helper
    mobileGrid: {
      gridTemplateColumns: "1fr",
      gap: "2rem",
    },
  };

  const isMobile =
    typeof window !== "undefined" ? window.matchMedia("(max-width: 900px)").matches : false;

  // ----- small hover helpers (inline styles => onMouseEnter/Leave) -----
  const hover = {
    linkIn: (e) => {
      e.currentTarget.style.color = "#3B82F6";
      e.currentTarget.style.transform = "translateX(2px)";
    },
    linkOut: (e, baseColor = "#60A5FA") => {
      e.currentTarget.style.color = baseColor;
      e.currentTarget.style.transform = "translateX(0px)";
    },
    navIn: (e) => {
      e.currentTarget.style.color = "#60A5FA";
      e.currentTarget.style.transform = "translateX(3px)";
    },
    navOut: (e) => {
      e.currentTarget.style.color = "#E5E7EB";
      e.currentTarget.style.transform = "translateX(0px)";
    },
    btnIn: (e) => {
      e.currentTarget.style.backgroundColor = "#1D4ED8";
      e.currentTarget.style.transform = "translateY(-1px)";
      e.currentTarget.style.boxShadow =
        "0 10px 20px -12px rgba(0,0,0,0.55)";
    },
    btnOut: (e) => {
      e.currentTarget.style.backgroundColor = "#2563EB";
      e.currentTarget.style.transform = "translateY(0px)";
      e.currentTarget.style.boxShadow = "none";
    },
    socialIn: (e) => {
      e.currentTarget.style.color = "#FFFFFF";
      e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.12)";
      e.currentTarget.style.transform = "translateY(-2px)";
      e.currentTarget.style.borderColor = "rgba(255,255,255,0.18)";
    },
    socialOut: (e) => {
      e.currentTarget.style.color = "#9CA3AF";
      e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.06)";
      e.currentTarget.style.transform = "translateY(0px)";
      e.currentTarget.style.borderColor = "rgba(255,255,255,0.10)";
    },
  };

  return (
    <footer style={styles.footer}>
      <div style={styles.container}>
        <div style={{ ...styles.topGrid, ...(isMobile ? styles.mobileGrid : null) }}>
          {/* À propos */}
          <div>
            <div style={styles.sectionTitle}>À propos</div>
            <p style={styles.aboutText}>
              AidFoundation BANQUE — Votre partenaire de confiance pour la gestion de vos finances en ligne.
            </p>

            <div style={styles.divider} />

            <a
              href="https://unpaidfinancial.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{ ...styles.inlineLink, transition: "all .2s ease" }}
              onMouseEnter={hover.linkIn}
              onMouseLeave={(e) => hover.linkOut(e, "#60A5FA")}
            >
              Visitez notre site principal
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                <path
                  d="M10 6H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                />
                <path
                  d="M14 4h6m0 0v6m0-6L10 14"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>
          </div>

          {/* Légal */}
          <div>
            <div style={styles.sectionTitle}>Légal</div>
            <ul style={styles.list}>
              <li>
                <Link
                  to="/cgu"
                  style={{ ...styles.navLink, transition: "all .2s ease" }}
                  onMouseEnter={hover.navIn}
                  onMouseLeave={hover.navOut}
                >
                  <svg
                    style={styles.icon}
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                    <line x1="16" y1="13" x2="8" y2="13" />
                    <line x1="16" y1="17" x2="8" y2="17" />
                  </svg>
                  Conditions Générales
                </Link>
              </li>

              <li>
                <Link
                  to="/confidentialite"
                  style={{ ...styles.navLink, transition: "all .2s ease" }}
                  onMouseEnter={hover.navIn}
                  onMouseLeave={hover.navOut}
                >
                  <svg
                    style={styles.icon}
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                  Confidentialité
                </Link>
              </li>

              <li>
                <Link
                  to="/donnees"
                  style={{ ...styles.navLink, transition: "all .2s ease" }}
                  onMouseEnter={hover.navIn}
                  onMouseLeave={hover.navOut}
                >
                  <svg
                    style={styles.icon}
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  </svg>
                  Protection des données
                </Link>
              </li>

              <li>
                <Link
                  to="/cookies"
                  style={{ ...styles.navLink, transition: "all .2s ease" }}
                  onMouseEnter={hover.navIn}
                  onMouseLeave={hover.navOut}
                >
                  <svg
                    style={styles.icon}
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-5-5 4 4 0 0 1-5-5" />
                    <path d="M8.5 8.5v.01" />
                    <path d="M16 15.5v.01" />
                    <path d="M12 12v.01" />
                    <path d="M11 17v.01" />
                    <path d="M7 14v.01" />
                  </svg>
                  Gestion des cookies
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <div style={styles.sectionTitle}>Contact</div>

            <div style={styles.contactRow}>
              <svg
                style={styles.icon}
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
              <div>
                <div style={styles.contactLabel}>Email</div>
                <a
                  href="mailto:contact@aidfoundation.eu"
                  style={{ ...styles.inlineLink, transition: "all .2s ease" }}
                  onMouseEnter={hover.linkIn}
                  onMouseLeave={(e) => hover.linkOut(e, "#60A5FA")}
                >
                  contact@aidfoundation.eu
                </a>
              </div>
            </div>

            <div style={styles.contactRow}>
              <svg
                style={styles.icon}
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
              <div>
                <div style={styles.contactLabel}>Téléphone</div>
                <a
                  href="tel:+33774137802"
                  style={{ ...styles.inlineLink, transition: "all .2s ease" }}
                  onMouseEnter={hover.linkIn}
                  onMouseLeave={(e) => hover.linkOut(e, "#60A5FA")}
                >
                  +33 7 74 13 78 02
                </a>
              </div>
            </div>

            <Link
              to="/connexion"
              style={{ ...styles.loginButton, transition: "all .2s ease" }}
              onMouseEnter={hover.btnIn}
              onMouseLeave={hover.btnOut}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3h7a3 3 0 0 1 3 3v1"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Espace Client
            </Link>

            <p style={{ ...styles.contactValue, marginTop: "0.75rem" }}>
              Besoin d’aide ? Écrivez-nous, on vous répond rapidement.
            </p>
          </div>
        </div>

        <div style={styles.bottom}>
          <p style={styles.copyright}>
            © {currentYear} AidFoundation BANQUE. Tous droits réservés.
          </p>

          <div style={styles.socials}>
            <a
              href="#"
              style={{ ...styles.socialBtn, transition: "all .2s ease" }}
              aria-label="Facebook"
              onMouseEnter={hover.socialIn}
              onMouseLeave={hover.socialOut}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" />
              </svg>
            </a>

            <a
              href="#"
              style={{ ...styles.socialBtn, transition: "all .2s ease" }}
              aria-label="Twitter"
              onMouseEnter={hover.socialIn}
              onMouseLeave={hover.socialOut}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
              </svg>
            </a>

            <a
              href="#"
              style={{ ...styles.socialBtn, transition: "all .2s ease" }}
              aria-label="LinkedIn"
              onMouseEnter={hover.socialIn}
              onMouseLeave={hover.socialOut}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
